(() => {
  const configElement = document.getElementById('grabbr-currency-config');
  if (!configElement) return;

  let config;
  try {
    config = JSON.parse(configElement.textContent);
  } catch {
    return;
  }

  const locale = config.locale || document.documentElement.lang || 'en';
  const currency = config.currency;
  let formatter;
  try {
    formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'symbol',
    });
  } catch {
    return;
  }

  const factor = 10 ** formatter.resolvedOptions().maximumFractionDigits;
  const widgetSelector = `.bogos-volume-discount-widget[data-bundle-id="${config.offerId}"]`;
  const marqueeSelector = '[data-grabbr-currency-marquee]';
  const templates = new WeakMap();
  const numberPattern = '[0-9٠-٩۰-۹]+(?:[.,٫٬][0-9٠-٩۰-۹]+)*';
  const localCurrencyNames = [...new Set([
    currency,
    ...formatter.formatToParts(1).filter(part => part.type === 'currency').map(part => part.value),
  ])].map(name => name.replace(/[.*+?^${}()|[\]\\]/gu, '\\$&')).join('|');
  const dollarNames = [
    'US\\s*\\$', 'USD\\b', '\\$', 'U\\.?S\\.?\\s+dollars?', 'American\\s+dollars?',
    'دولار(?:ات|ًا|ا)?(?:\\s+أمريكي(?:ة|اً|ا)?)?', 'अमेरिकी\\s+डॉलर',
    '米ドル', '미국\\s*달러', '달러', 'доллар(?:а|ов)?\\s+США',
    'ABD\\s+dolar[ıi]', 'ดอลลาร์สหรัฐ', 'امریکی\\s+ڈالر',
    'dólares?\\s+(?:estadounidenses|americanos)', 'dollars?\\s+(?:US|américains)', localCurrencyNames,
  ].join('|');
  const moneyPattern = new RegExp(
    `[\\u2066-\\u2069]*(?:미화\\s*${numberPattern}\\s*달러|(?:US\\s*\\$|USD\\s*|\\$|${localCurrencyNames})\\s*${numberPattern}|${numberPattern}\\s*(?:${dollarNames})|\\{\\{discountAmount\\}\\})[\\u2066-\\u2069]*`,
    'giu',
  );

  function sourceNumber(text) {
    const match = text.match(new RegExp(numberPattern, 'u'));
    if (!match) return null;
    let value = match[0].replace(/[٠-٩۰-۹]/gu, digit => {
      const code = digit.charCodeAt(0);
      return String(code >= 0x6f0 ? code - 0x6f0 : code - 0x660);
    }).replace(/٬/gu, '').replace(/٫/gu, '.');
    const comma = value.lastIndexOf(',');
    const dot = value.lastIndexOf('.');
    if (comma > dot) value = value.replace(/\./gu, '').replace(',', '.');
    else value = value.replace(/,/gu, '');
    const result = Number(value);
    return Number.isFinite(result) && result >= 0 ? result : null;
  }

  function savings(item) {
    if (!item) return null;
    const originalText = item.getAttribute('data-bogos-tier-original-price');
    const discountedText = item.getAttribute('data-bogos-tier-discount-price');
    if (originalText === null || discountedText === null || originalText === '' || discountedText === '') return null;
    const original = Number(originalText);
    const discounted = Number(discountedText);
    if (!Number.isFinite(original) || !Number.isFinite(discounted) || original < discounted || discounted < 0) return null;
    return (Math.round(original * factor) - Math.round(discounted * factor)) / factor;
  }

  function conversionRate() {
    if (currency === config.baseCurrency) return 1;
    const platformCurrency = window.Shopify?.currency;
    const rate = Number(platformCurrency?.rate);
    if (platformCurrency?.active === currency && Number.isFinite(rate) && rate > 0) return rate;
    return null;
  }

  function replaceText(element, amountForMatch, hideIfUnavailable = true) {
    if (!element) return;
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
    let node;
    let unavailable = false;
    while ((node = walker.nextNode())) {
      let state = templates.get(node);
      if (!state || node.nodeValue !== state.applied) {
        state = { source: node.nodeValue, applied: node.nodeValue };
        templates.set(node, state);
      }
      const output = state.source.replace(moneyPattern, match => {
        const amount = amountForMatch(match);
        if (amount === null || !Number.isFinite(amount) || amount < 0) {
          unavailable = true;
          return hideIfUnavailable ? match : '';
        }
        return `\u2068${formatter.format(amount)}\u2069`;
      });
      if (node.nodeValue !== output) node.nodeValue = output;
      state.applied = output;
    }
    element.toggleAttribute('data-grabbr-currency-unavailable', unavailable && hideIfUnavailable);
    element.setAttribute('data-grabbr-currency-ready', '');
    element.removeAttribute('data-grabbr-currency-pending');
  }

  function update() {
    const rate = conversionRate();
    for (const marquee of document.querySelectorAll(marqueeSelector)) {
      for (const text of marquee.querySelectorAll('[data-grabbr-currency-text]')) {
        replaceText(text, match => {
          const amount = sourceNumber(match);
          return amount === null || rate === null ? null : amount * rate;
        });
      }
      marquee.setAttribute('data-grabbr-display-currency', currency);
    }

    for (const widget of document.querySelectorAll(widgetSelector)) {
      for (const item of widget.querySelectorAll('.bogos-volume-discount-item')) {
        const amount = savings(item);
        replaceText(item.querySelector('.bogos-volume-discount-label'), () => amount);
      }
      const selected = widget.querySelector('.bogos-volume-discount-tier-input:checked');
      const amount = savings(selected?.closest('.bogos-volume-discount-item'));
      replaceText(widget.querySelector('.bogos-volume-discount-button-add'), () => amount, false);
      widget.setAttribute('data-grabbr-display-currency', currency);
    }
  }

  let scheduled = false;
  let observer;
  const observerOptions = {
    childList: true,
    subtree: true,
    characterData: true,
    attributes: true,
    attributeFilter: ['data-bogos-tier-original-price', 'data-bogos-tier-discount-price'],
  };

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(() => {
      scheduled = false;
      observer.disconnect();
      update();
      observer.observe(document.body, observerOptions);
    });
  }

  function relevant(record) {
    const element = record.target.nodeType === Node.ELEMENT_NODE ? record.target : record.target.parentElement;
    if (element?.closest(`${marqueeSelector},${widgetSelector} .bogos-volume-discount-item,${widgetSelector} .bogos-volume-discount-button-add`)) return true;
    if (record.type !== 'childList') return false;
    return [...record.addedNodes].some(node => node.nodeType === Node.ELEMENT_NODE && (
      node.matches(`${marqueeSelector},${widgetSelector}`) || node.querySelector(`${marqueeSelector},${widgetSelector}`)
    ));
  }

  function start() {
    update();
    document.documentElement.setAttribute('data-grabbr-currency-active', '');
    observer = new MutationObserver(records => {
      if (records.some(relevant)) schedule();
    });
    observer.observe(document.body, observerOptions);
    document.addEventListener('change', event => {
      if (event.target.closest(widgetSelector)) schedule();
    });
    document.addEventListener('shopify:section:load', schedule);
    window.addEventListener('pageshow', schedule);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
